import React, { useState, useCallback } from 'react';
import {
  Sparkles,
  Settings,
  Plus,
  ChevronLeft,
  ChevronRight,
  BookOpen,
  PenTool,
} from 'lucide-react';
import { AppLayout } from '../../components/AppLayout';
import { Card, Modal } from '../../components/ui';
import { PhotoUpload } from '../../components/MorningPages/PhotoUpload';
import { PhotoGallery } from '../../components/MorningPages/PhotoGallery';
import { OcrReviewEdit } from '../../components/MorningPages/OcrReviewEdit';
import { InsightsDashboard } from '../../components/MorningPages/InsightsDashboard';
import { PrivacySettings } from '../../components/MorningPages/PrivacySettings';
import { AhaMomentContainer, useAhaMoment } from '../../components/MorningPages/AhaMomentNotification';
import { getAssetUrl, morningPagesService, type MorningPageDto, type MorningPageInsightDto } from '../../services';
import './MorningPages.css';

type ViewType = 'gallery' | 'upload' | 'detail' | 'insights' | 'settings';

interface MorningPagesProps {
  isPremium?: boolean;
}

export const MorningPages: React.FC<MorningPagesProps> = ({
  isPremium = true,
}) => {
  const [currentView, setCurrentView] = useState<ViewType>('gallery');
  const [selectedPage, setSelectedPage] = useState<MorningPageDto | null>(null);
  const [pageInsights, setPageInsights] = useState<MorningPageInsightDto[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  const [detailPhotoIndex, setDetailPhotoIndex] = useState(0);
  const [galleryRefreshKey, setGalleryRefreshKey] = useState(0);

  // Aha Moment notifications
  const {
    notifications,
    dismissAhaMoment,
    defaultAutoHide,
  } = useAhaMoment({ maxNotifications: 3 });

  const handleUploadComplete = useCallback((photoUrl: string, pageId: string) => {
    setShowUploadModal(false);
    setCurrentView('gallery');
    setGalleryRefreshKey((prev) => prev + 1);
  }, []);

  const handlePageClick = useCallback(async (page: MorningPageDto) => {
    setSelectedPage(page);
    setPageInsights([]);
    setDetailPhotoIndex(0);
    setCurrentView('detail');

    // Load existing insights if the page was already analyzed
    if (page.hasAnalysis || page.isAnalyzed) {
      try {
        const insights = await morningPagesService.getInsights(page.id);
        setPageInsights(insights);
      } catch {
        // Silently fail - insights will just not show
      }
    }
  }, []);

  const handleBack = useCallback(() => {
    if (currentView === 'detail') {
      setSelectedPage(null);
      setCurrentView('gallery');
    } else {
      setCurrentView('gallery');
    }
  }, [currentView]);

  const handleInsightClick = useCallback((insight: MorningPageInsightDto) => {
    // Navigate to insight detail or show modal
    console.log('Insight clicked:', insight);
  }, []);

  const handleAnalyze = useCallback(async () => {
    if (!selectedPage) return;

    setIsAnalyzing(true);
    setAnalyzeError(null);

    try {
      const result = await morningPagesService.analyze(selectedPage.id, true);
      // Update the selected page with analysis results
      setSelectedPage({
        ...selectedPage,
        hasAnalysis: true,
        isAnalyzed: true,
        analysisJson: result.analysisJson,
      });
      // Store the insights
      setPageInsights(result.insights || []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to analyze morning page';
      setAnalyzeError(errorMessage);
      console.error('Analysis error:', err);
    } finally {
      setIsAnalyzing(false);
    }
  }, [selectedPage]);

  const isSubView = currentView === 'detail' || currentView === 'settings';
  const isMainView = !isSubView;

  const renderHeader = () => {
    if (isSubView) return null;

    return (
      <div className="morning-pages__header">
        <div className="morning-pages__header-top">
          <div className="morning-pages__title-area">
            <div className="morning-pages__title-icon">
              <PenTool size={20} />
            </div>
            <div>
              <h1 className="morning-pages__title">Your Pages</h1>
              <p className="morning-pages__subtitle">Your creative journal</p>
            </div>
          </div>
          <button
            className="morning-pages__settings-btn"
            onClick={() => setCurrentView('settings')}
            aria-label="Privacy Settings"
          >
            <Settings size={18} />
          </button>
        </div>

        <nav className="morning-pages__pill-tabs" role="tablist">
          <button
            role="tab"
            aria-selected={currentView === 'gallery'}
            className={`morning-pages__pill-tab ${currentView === 'gallery' ? 'morning-pages__pill-tab--active' : ''}`}
            onClick={() => setCurrentView('gallery')}
          >
            <BookOpen size={16} />
            Journal
          </button>
          <button
            role="tab"
            aria-selected={currentView === 'insights'}
            className={`morning-pages__pill-tab ${currentView === 'insights' ? 'morning-pages__pill-tab--active' : ''}`}
            onClick={() => setCurrentView('insights')}
          >
            <Sparkles size={16} />
            Insights
          </button>
        </nav>
      </div>
    );
  };

  const renderContent = () => {
    switch (currentView) {
      case 'gallery':
        return (
          <div className="morning-pages__gallery-view">
            <PhotoGallery
              onPhotoClick={handlePageClick}
              onUploadClick={() => setShowUploadModal(true)}
              showDelete={true}
              refreshKey={galleryRefreshKey}
            />
          </div>
        );

      case 'upload':
        return (
          <div className="morning-pages__upload-view">
            <PhotoUpload
              onUploadComplete={handleUploadComplete}
              onCancel={() => setCurrentView('gallery')}
            />
          </div>
        );

      case 'detail':
        if (!selectedPage) return null;
        const detailPhotos = selectedPage.photoUrls?.length > 0
          ? selectedPage.photoUrls
          : [selectedPage.photoUrl];
        const currentDetailPhoto = detailPhotos[detailPhotoIndex] || detailPhotos[0];
        return (
          <div className="morning-pages__detail-view">
            <div className="morning-pages__detail-header">
              <h2 className="morning-pages__detail-title">
                {new Date(selectedPage.date).toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
                {detailPhotos.length > 1 && (
                  <span className="morning-pages__photo-counter">
                    {detailPhotoIndex + 1} / {detailPhotos.length} pages
                  </span>
                )}
              </h2>
            </div>

            <div className="morning-pages__detail-content">
              {/* Image Section */}
              <Card variant="default" padding="none" className="morning-pages__image-card">
                <div className="morning-pages__image-container">
                  <img
                    src={getAssetUrl(currentDetailPhoto)}
                    alt={`Morning page from ${selectedPage.date} - page ${detailPhotoIndex + 1}`}
                    className="morning-pages__detail-image"
                  />
                  {detailPhotos.length > 1 && (
                    <>
                      <button
                        className="morning-pages__photo-nav morning-pages__photo-nav--prev"
                        onClick={() => setDetailPhotoIndex((prev) => (prev > 0 ? prev - 1 : detailPhotos.length - 1))}
                        aria-label="Previous page"
                      >
                        <ChevronLeft size={24} />
                      </button>
                      <button
                        className="morning-pages__photo-nav morning-pages__photo-nav--next"
                        onClick={() => setDetailPhotoIndex((prev) => (prev < detailPhotos.length - 1 ? prev + 1 : 0))}
                        aria-label="Next page"
                      >
                        <ChevronRight size={24} />
                      </button>
                      <div className="morning-pages__photo-dots">
                        {detailPhotos.map((_, i) => (
                          <button
                            key={i}
                            className={`morning-pages__photo-dot ${i === detailPhotoIndex ? 'morning-pages__photo-dot--active' : ''}`}
                            onClick={() => setDetailPhotoIndex(i)}
                            aria-label={`Go to page ${i + 1}`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </Card>

              {/* OCR Review Section */}
              <div className="morning-pages__ocr-section">
                <OcrReviewEdit
                  morningPage={selectedPage}
                  onOcrUpdate={(updated) => setSelectedPage(updated)}
                  onAnalyze={handleAnalyze}
                />
                {analyzeError && (
                  <div className="morning-pages__analyze-error">
                    <span>{analyzeError}</span>
                  </div>
                )}
              </div>

              {/* Insights Section (if analyzed) */}
              {selectedPage.hasAnalysis && pageInsights.length > 0 && (
                <div className="morning-pages__page-insights">
                  <h3 className="morning-pages__section-title">
                    <Sparkles size={18} />
                    Insights from this page
                  </h3>
                  <div className="morning-pages__insights-grid">
                    {pageInsights.map((insight) => (
                      <Card
                        key={insight.id}
                        variant="outlined"
                        padding="md"
                        className={`morning-pages__insight-card morning-pages__insight-card--${insight.insightType.toLowerCase()}`}
                      >
                        <div className="morning-pages__insight-header">
                          <span className="morning-pages__insight-type">{insight.insightType}</span>
                          {insight.isActionable && (
                            <span className="morning-pages__insight-badge">Actionable</span>
                          )}
                        </div>
                        <h4 className="morning-pages__insight-title">{insight.title}</h4>
                        <p className="morning-pages__insight-content">{insight.content}</p>
                        {insight.relatedProjectName && (
                          <span className="morning-pages__insight-project">
                            Related to: {insight.relatedProjectName}
                          </span>
                        )}
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 'insights':
        return (
          <div className="morning-pages__insights-view">
            <InsightsDashboard defaultTab="insights" />
          </div>
        );

      case 'settings':
        return (
          <div className="morning-pages__settings-view">
            <PrivacySettings
              isPremium={isPremium}
              onSave={async (settings) => {
                // Save settings
                console.log('Saving settings:', settings);
              }}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <AppLayout
      pageTitle="Morning Pages"
      showBackButton={isSubView}
      onBack={handleBack}
      backLabel="Back"
    >
      <div className="morning-pages">
        {renderHeader()}

        <main className="morning-pages__main">
          {renderContent()}
        </main>

        {/* Floating Upload Button */}
        {isMainView && (
          <button
            className="morning-pages__fab"
            onClick={() => setShowUploadModal(true)}
            aria-label="Upload morning pages"
          >
            <Plus size={24} />
          </button>
        )}

        {/* Upload Modal */}
        <Modal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          title="Upload Morning Pages"
          size="md"
        >
          <PhotoUpload
            onUploadComplete={handleUploadComplete}
            onCancel={() => setShowUploadModal(false)}
          />
        </Modal>

        {/* Aha Moment Notifications */}
        <AhaMomentContainer
          notifications={notifications}
          onDismiss={dismissAhaMoment}
          onClick={handleInsightClick}
          autoHideAfter={defaultAutoHide}
          position="bottom-right"
        />

        {/* Full-page Analyzing Overlay */}
        {isAnalyzing && (
          <div className="morning-pages__analyzing-overlay">
            <div className="morning-pages__analyzing-card">
              <div className="morning-pages__analyzing-spinner">
                <Sparkles size={28} />
              </div>
              <h3 className="morning-pages__analyzing-title">Analyzing your page</h3>
              <p className="morning-pages__analyzing-subtitle">
                AI is reading your morning pages and discovering insights...
              </p>
              <div className="morning-pages__analyzing-dots">
                <span />
                <span />
                <span />
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default MorningPages;
